import { FC } from "react";
import Logo from "shared/Logo/Logo";
import Navigation from "shared/Navigation/Navigation";
import MenuBar from "shared/MenuBar/MenuBar";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useLocation } from "react-router-dom";
import { useData } from "data/data-provider";

export interface MainNav1Props {
  className?: string;
}

// Header layout variants, chosen in the FMS website builder and stored on
// CompanyWebsiteDetail.header_layout:
//   "classic"      → logo left, nav right, "Book Now" CTA pinned right; default
//   "split_cta"    → logo left, nav centered, "Book Now" CTA pinned right
//   "drawer_right" → logo left, "Book Now" CTA + menu button right; nav lives
//                    in the slide-out panel from the right (no inline desktop nav)
//
// The "Book Now" CTA is the primary conversion action and is shown in every
// layout (1NOW-1240) — only its placement and the nav arrangement differ.
type HeaderVariant = "classic" | "split_cta" | "drawer_right";

const MainNav: FC<MainNav1Props> = ({ className = "" }) => {
  // `!` on the show/hide variants below: see shared/Navigation/Navigation.tsx
  // (Meta Event Setup Tool injects a competing Tailwind sheet).
  const location = useLocation();
  const { ourCompany, companyDetails } = useData();

  // Source of truth for the layout is the FMS-set `header_layout`. A
  // `?headerVariant=` query param overrides it for live preview (handy
  // before the tenant has saved a choice). Defaults to "classic" so every
  // existing tenant keeps the original Design A bar untouched.
  const params = new URLSearchParams(location.search);
  const variant: HeaderVariant =
    (params.get("headerVariant") as HeaderVariant) ||
    (companyDetails?.header_layout as HeaderVariant) ||
    "classic";

  if (variant === "drawer_right") {
    /* ─────────────────────────────────────────────────────────────
       Drawer right — logo left · Book Now CTA + menu button right. The
       existing MenuBar trigger is forced visible at ALL breakpoints, so
       the nav lives entirely in the slide-out panel instead of inline;
       the Book Now CTA stays in the bar (from sm up) so the primary
       conversion action is reachable without opening the drawer.
       ───────────────────────────────────────────────────────────── */
    return (
      <div className={`nc-MainNav1 relative z-10 ${className}`}>
        <div
          className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center`}
        >
          <div className="flex justify-start items-center space-x-4 sm:space-x-10">
            <Logo />
          </div>

          <div className="flex items-center space-x-4">
            <ButtonPrimary
              href="/vehicles"
              className="hidden sm:!inline-flex"
              sizeClass="px-5 py-2.5"
            >
              Book Now
            </ButtonPrimary>
            <MenuBar />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "split_cta") {
    /* ─────────────────────────────────────────────────────────────
       Split + CTA — logo left · nav dead-center · Book Now right.
       The nav is absolutely centered so it stays centered regardless
       of logo / CTA width. Below lg the nav collapses into the
       hamburger (same as classic) — the nav links only appear at lg
       (Navigation is `hidden lg:!flex`), so the menu trigger MUST mirror
       that with `lg:hidden`. Using `md:hidden` here left a 768–1023px
       dead zone (iPad Air) where neither the nav nor the hamburger
       showed (1NOW-1238). The CTA stays visible from sm up. */
    return (
      <div className={`nc-MainNav1 relative z-10 ${className}`}>
        <div
          className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center`}
        >
          <div className="flex justify-start items-center space-x-4 sm:space-x-10">
            <Logo />
          </div>

          <div className="hidden lg:!flex items-center absolute left-1/2 -translate-x-1/2">
            <Navigation />
          </div>

          <div className="flex items-center space-x-4">
            <ButtonPrimary
              href="/vehicles"
              className="hidden sm:!inline-flex"
              sizeClass="px-5 py-2.5"
            >
              Book Now
            </ButtonPrimary>
            <div className="flex lg:!hidden items-center">
              <div className="px-0.5" />
              <MenuBar />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────────────────────────────────────────────────────────────
     Classic (default) — logo left · nav + Book Now CTA right. This is
     what every live tenant (header_layout=null) renders. The desktop
     nav, the persistent Book Now CTA (from sm up) and the mobile menu
     trigger share one right-side cluster so the CTA sits beside the nav
     on desktop and beside the hamburger on mobile (1NOW-1240).
     ─────────────────────────────────────────────────────────────── */
  return (
    <div className={`nc-MainNav1 relative z-10 ${className}`}>
      <div
        className={`px-4 lg:container ${ourCompany?.company?.id === 1592 ? "py-0" : "py-2"} relative flex justify-between items-center`}
      >
        <div className="flex justify-start items-center space-x-4 sm:space-x-10">
          <Logo />
        </div>

        <div className="flex items-center space-x-4">
          <div className="hidden md:!flex items-center">
            <Navigation />
          </div>
          <ButtonPrimary
            href="/vehicles"
            className="hidden sm:!inline-flex"
            sizeClass="px-5 py-2.5"
          >
            Book Now
          </ButtonPrimary>
          <div className="flex lg:!hidden items-center">
            <MenuBar />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainNav;
